import {
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Box,
    Select,
    MenuItem,
    FormControl,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import DeleteEventDialog from './DeleteEventDialog';
import DeleteEventGroupDialog from './DeleteEventGroupDialog';
import { Event, EventGroup, queryCurrentUser } from '../schema';
import { useEventsList, useEventGroupList } from '../context';
import { getOneLink } from '../utils';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { usePermissions } from '../hooks/usePermissions';

type SortMode = 'chronological' | 'alphabetical' | 'custom';
type SortDirection = 'asc' | 'desc';

// Helper to parse JSON safely
const parseJsonSet = (value: unknown): Set<string> => {
    if (typeof value === 'string') {
        try {
            return new Set(JSON.parse(value));
        } catch {
            return new Set();
        }
    }
    return new Set();
};

interface EventsListInnerProps {
    userSettings: any;
    currentUser: any;
}

function EventsListInner({ userSettings, currentUser }: EventsListInnerProps) {
    const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
    const [deleteEventGroupDialogOpen, setDeleteEventGroupDialogOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<Event | undefined>(undefined);
    const [eventGroupToDelete, setEventGroupToDelete] = useState<EventGroup | undefined>(undefined);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    // Initialize state from userSettings (component remounts when userSettings.id changes via key prop)
    const [sortMode, setSortModeState] = useState<SortMode>(() => (userSettings?.eventSortMode as SortMode) || 'chronological');
    const [sortDirection, setSortDirectionState] = useState<SortDirection>(
        () => (userSettings?.eventSortDirection as SortDirection) || 'asc'
    );
    const [pinnedEventIds, setPinnedEventIdsState] = useState<Set<string>>(() => parseJsonSet(userSettings?.pinnedEventIds));
    const [pinnedGroupIds, setPinnedGroupIdsState] = useState<Set<string>>(() => parseJsonSet(userSettings?.pinnedGroupIds));

    // Track whether user has made any changes (to avoid persisting on initial mount)
    const hasUserChangesRef = useRef(false);

    // Wrapped setters that mark user changes
    const setSortMode = useCallback((mode: SortMode) => {
        hasUserChangesRef.current = true;
        setSortModeState(mode);
    }, []);
    const setSortDirection = useCallback((direction: SortDirection) => {
        hasUserChangesRef.current = true;
        setSortDirectionState(direction);
    }, []);
    const setPinnedEventIds = useCallback((updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
        hasUserChangesRef.current = true;
        setPinnedEventIdsState(updater);
    }, []);
    const setPinnedGroupIds = useCallback((updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
        hasUserChangesRef.current = true;
        setPinnedGroupIdsState(updater);
    }, []);

    const navigate = useNavigate();
    const { canAccessEvent } = usePermissions();
    const allEvents = useEventsList();
    const eventGroups = useEventGroupList();

    const events = useMemo(() => allEvents.filter(event => canAccessEvent(event.id)), [allEvents, canAccessEvent]);

    const { accessibleGroups, finalUngroupedEvents } = useMemo(() => {
        const groupAccessMap = new Map<string, Event[]>();

        eventGroups.forEach(group => {
            const groupEvents = events.filter(e => getOneLink(e.group)?.id === group.id);
            if (groupEvents.length > 0) {
                groupAccessMap.set(group.id, groupEvents);
            }
        });

        const groupsToShow = eventGroups.filter(group => {
            const groupEvents = groupAccessMap.get(group.id) || [];
            return groupEvents.length >= 2;
        });

        const ungrouped = events.filter(event => {
            const groupLink = getOneLink(event.group);
            if (!groupLink) return true; // No group

            const groupEvents = groupAccessMap.get(groupLink.id) || [];
            return groupEvents.length < 2; // Only 1 accessible event in this group
        });

        return {
            accessibleGroups: groupsToShow,
            finalUngroupedEvents: ungrouped,
        };
    }, [events, eventGroups]);

    const maxOrder = events.reduce((max, event) => Math.max(max, event.order as number), 0) || 0;

    // Persist user preferences when they change (only after user-initiated changes)
    const userSettingsId = userSettings?.id;
    const currentUserId = currentUser?.id;
    useEffect(() => {
        // Only persist if user has made changes (not on initial mount)
        if (!currentUserId || !hasUserChangesRef.current) return;

        // Reset flag immediately to prevent re-triggering
        hasUserChangesRef.current = false;

        // Create or update settings
        if (userSettingsId) {
            // Update existing settings
            db.transact([
                db.tx.userSettings[userSettingsId].update({
                    eventSortMode: sortMode,
                    eventSortDirection: sortDirection,
                    pinnedEventIds: JSON.stringify(Array.from(pinnedEventIds)),
                    pinnedGroupIds: JSON.stringify(Array.from(pinnedGroupIds)),
                }),
            ]);
        } else {
            // Create new settings linked to user
            const newSettingsId = id();
            db.transact([
                db.tx.userSettings[newSettingsId]
                    .update({
                        eventSortMode: sortMode,
                        eventSortDirection: sortDirection,
                        pinnedEventIds: JSON.stringify(Array.from(pinnedEventIds)),
                        pinnedGroupIds: JSON.stringify(Array.from(pinnedGroupIds)),
                    })
                    .link({ user: currentUserId }),
            ]);
        }
    }, [sortMode, sortDirection, pinnedEventIds, pinnedGroupIds, currentUserId, userSettingsId]);

    const handleCloseDeleteEventDialog = () => {
        setDeleteEventDialogOpen(false);
    };

    const handleCloseDeleteEventGroupDialog = () => {
        setDeleteEventGroupDialogOpen(false);
    };

    const handleClickDeleteEvent = (event: Event) => {
        setEventToDelete(event);
        setDeleteEventDialogOpen(true);
    };

    const handleClickDeleteGroup = (eventGroup: EventGroup) => {
        setEventGroupToDelete(eventGroup);
        setDeleteEventGroupDialogOpen(true);
    };

    const toggleGroup = (groupId: string) => {
        setOpenGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    const togglePinEvent = (eventId: string) => {
        setPinnedEventIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(eventId)) {
                newSet.delete(eventId);
            } else {
                newSet.add(eventId);
            }
            return newSet;
        });
    };

    const togglePinGroup = (groupId: string) => {
        setPinnedGroupIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const getEventDate = (event: Event): Date | null => {
        return event.startDate ? new Date(event.startDate as string | number | Date) : null;
    };

    const getGroupDate = useCallback(
        (group: EventGroup): Date | null => {
            const groupEvents = events.filter(e => getOneLink(e.group)?.id === group.id);
            if (groupEvents.length === 0) return null;

            const dates = groupEvents.map(e => getEventDate(e)).filter((d): d is Date => d !== null);

            if (dates.length === 0) return null;

            if (sortDirection === 'asc') {
                // return earliest date
                return new Date(Math.min(...dates.map(d => d.getTime())));
            } else {
                // return latest date
                return new Date(Math.max(...dates.map(d => d.getTime())));
            }
        },
        [events, sortDirection]
    );

    // In-group event sorting
    const sortGroupEvents = (groupId: string): Event[] => {
        let groupEvents = events.filter(event => getOneLink(event.group)?.id === groupId);

        if (sortMode === 'chronological') {
            groupEvents.sort((a, b) => {
                const dateA = getEventDate(a);
                const dateB = getEventDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            });
        } else if (sortMode === 'alphabetical') {
            groupEvents.sort((a, b) => {
                const nameA = ((a.name as string) || '').toLowerCase();
                const nameB = ((b.name as string) || '').toLowerCase();
                return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
        } else if (sortMode === 'custom') {
            const pinned = groupEvents.filter(e => pinnedEventIds.has(e.id));
            const unpinned = groupEvents.filter(e => !pinnedEventIds.has(e.id));
            groupEvents = [...pinned, ...unpinned];
        }

        return groupEvents;
    };

    const { sortedGroups, sortedUngroupedEvents } = useMemo(() => {
        let processedGroups = [...accessibleGroups];
        let processedEvents = [...finalUngroupedEvents];

        if (sortMode === 'chronological') {
            // Sort folders by date
            processedGroups.sort((a, b) => {
                const dateA = getGroupDate(a);
                const dateB = getGroupDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            });

            // Sort single-events by date
            processedEvents.sort((a, b) => {
                const dateA = getEventDate(a);
                const dateB = getEventDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            });
        } else if (sortMode === 'alphabetical') {
            // Sort folders alphabetically
            processedGroups.sort((a, b) => {
                const nameA = ((a.name as string) || '').toLowerCase();
                const nameB = ((b.name as string) || '').toLowerCase();
                return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });

            // Sort single-events alphabetically
            processedEvents.sort((a, b) => {
                const nameA = ((a.name as string) || '').toLowerCase();
                const nameB = ((b.name as string) || '').toLowerCase();
                return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
        }

        // custom mode
        if (sortMode === 'custom') {
            const pinnedGroups = processedGroups.filter(g => pinnedGroupIds.has(g.id));
            const unpinnedGroups = processedGroups.filter(g => !pinnedGroupIds.has(g.id));
            processedGroups = [...pinnedGroups, ...unpinnedGroups];

            const pinnedEvents = processedEvents.filter(e => pinnedEventIds.has(e.id));
            const unpinnedEvents = processedEvents.filter(e => !pinnedEventIds.has(e.id));
            processedEvents = [...pinnedEvents, ...unpinnedEvents];
        }

        return {
            sortedGroups: processedGroups,
            sortedUngroupedEvents: processedEvents,
        };
    }, [accessibleGroups, finalUngroupedEvents, sortMode, sortDirection, pinnedEventIds, pinnedGroupIds, getGroupDate]);

    return (
        <>
            <DeleteEventDialog event={eventToDelete} open={deleteEventDialogOpen} onClose={handleCloseDeleteEventDialog} />
            <DeleteEventGroupDialog
                eventGroup={eventGroupToDelete}
                maxOrder={maxOrder}
                open={deleteEventGroupDialogOpen}
                onClose={handleCloseDeleteEventGroupDialog}
            />
            <Box sx={{ minWidth: '300px', width: '100%', maxWidth: '400px', mt: 3 }}>
                {/* Filter Controls */}
                <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)}>
                            <MenuItem value="chronological">Chronological</MenuItem>
                            <MenuItem value="alphabetical">Alphabetical</MenuItem>
                            {/* <MenuItem value="custom">Custom</MenuItem> */}
                        </Select>
                    </FormControl>

                    {(sortMode === 'chronological' || sortMode === 'alphabetical') && (
                        <ToggleButtonGroup
                            value={sortDirection}
                            exclusive
                            onChange={(_, newDirection) => {
                                if (newDirection !== null) setSortDirection(newDirection);
                            }}
                            size="small"
                        >
                            <ToggleButton value="asc" aria-label="ascending">
                                <ArrowUpwardIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="desc" aria-label="descending">
                                <ArrowDownwardIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>

                <List style={styles.eventsList}>
                    {sortedGroups.map(group => (
                        <div key={group.id}>
                            <ListItem
                                disablePadding
                                secondaryAction={
                                    <Box sx={{ display: 'flex', gap: 0 }}>
                                        {sortMode === 'custom' && (
                                            <IconButton onClick={() => togglePinGroup(group.id)} edge="end" aria-label="pin">
                                                {pinnedGroupIds.has(group.id) ? <StarIcon /> : <StarBorderIcon />}
                                            </IconButton>
                                        )}
                                        <IconButton onClick={() => handleClickDeleteGroup(group)} edge="end" aria-label="delete">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <IconButton onClick={() => toggleGroup(group.id)} size="small">
                                    {openGroups[group.id] ? <ExpandMore /> : <ChevronRight />}
                                </IconButton>
                                <ListItemButton onClick={() => navigate(`/group/${group.id}`)} sx={{ pl: 1 }}>
                                    <FolderIcon />
                                    <ListItemText sx={{ ml: 1 }}>{group.name as string}</ListItemText>
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={openGroups[group.id]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {sortGroupEvents(group.id).map(event => (
                                        <ListItem
                                            key={event.id}
                                            disablePadding
                                            style={styles.eventItem}
                                            sx={{ pl: 4 }}
                                            secondaryAction={
                                                <Box sx={{ display: 'flex', gap: 0 }}>
                                                    {sortMode === 'custom' && (
                                                        <IconButton onClick={() => togglePinEvent(event.id)} edge="end" aria-label="pin">
                                                            {pinnedEventIds.has(event.id) ? <StarIcon /> : <StarBorderIcon />}
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        onClick={() => handleClickDeleteEvent(event)}
                                                        edge="end"
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            }
                                        >
                                            <ListItemButton onClick={() => navigate(`/event/${event.id}`)}>
                                                <ListItemText>{event.name as string}</ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </div>
                    ))}

                    {sortedUngroupedEvents.map(event => (
                        <ListItem
                            key={event.id}
                            disablePadding
                            style={styles.eventItem}
                            sx={{ pl: 1 }}
                            secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0 }}>
                                    {sortMode === 'custom' && (
                                        <IconButton onClick={() => togglePinEvent(event.id)} edge="end" aria-label="pin">
                                            {pinnedEventIds.has(event.id) ? <StarIcon /> : <StarBorderIcon />}
                                        </IconButton>
                                    )}
                                    <IconButton onClick={() => handleClickDeleteEvent(event)} edge="end" aria-label="delete">
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <ListItemButton onClick={() => navigate(`/event/${event.id}`)}>
                                <ListItemText>{event.name as string}</ListItemText>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Box>
        </>
    );
}

// Export wrapper - uses key pattern to remount inner component when userSettings loads
export default function EventsList() {
    const { data: userData } = queryCurrentUser();
    const currentUser = userData?.$users?.[0];
    const userSettings = currentUser?.settings as any;

    // Key on userSettings.id causes remount when settings first load,
    // allowing useState initializers to read the hydrated values
    return <EventsListInner key={userSettings?.id ?? 'no-settings'} userSettings={userSettings} currentUser={currentUser} />;
}

const styles: Record<string, React.CSSProperties> = {
    eventsList: {},
    eventItem: {},
};
